<template>
  <v-row>
    <v-col cols="12" class="source-title">
      <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }">
        <h2 class="ml-2 title">Business Directory Metrics</h2>
      </div>
    </v-col>
  </v-row>

  <v-row class="mt-2">
    <!-- Reviews and Ratings Chart -->
    <v-col v-if="chartHasNonZeroValues(ratingsChart)" cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.reviewsAndRatingLoading">
            <LineChart v-bind="ratingsChart" :column-width="40" class="no-axis-label-in-tooltip" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Reviews Chart -->
    <v-col v-if="chartHasNonZeroValues(reviewsChart)" cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.reviewsAndRatingLoading">
            <LineChart v-bind="reviewsChart" :column-width="40" class="no-axis-label-in-tooltip" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Reviews Distribution Chart -->
    <v-col
      v-if="chartHasNonZeroValues(reviewsDistributionChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
      class="page-break"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.reviewsDistributionLoading">
            <GroupedStackedChart
              v-bind="reviewsDistributionChart"
              :show-labels="true"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Reviews by Sentiment Chart -->
    <v-col v-if="chartHasNonZeroValues(reviewsBySentimentChart)" cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.reviewsDistributionLoading">
            <ColumnChart v-bind="reviewsBySentimentChart" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Response Rate Chart -->
    <v-col v-if="chartHasNonZeroValues(responseRateChart)" cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.reviewsAndRatingLoading">
            <GroupedStackedChart v-bind="responseRateChart" class="no-axis-label-in-tooltip" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Reviews and Ratings by Source Chart -->
    <v-col
      v-if="chartHasNonZeroValues(reviewsAndRatingsBySourceChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.reviewsAndRatingsBySourceLoading">
            <ColumnChart v-bind="reviewsAndRatingsBySourceChart" :show-labels="true" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="12">
      <h2>Listings Report</h2>
    </v-col>
    <v-col cols="12">
      <v-skeleton-loader
        :loading="state.directoryListingLoading"
        type="list-item"
        style="margin: auto"
        v-if="state.directoryListingLoading"
      ></v-skeleton-loader>
      <span style="font-size: 1rem" v-else>
        Business Name: <strong>{{ state.directoryListings?.businessName }}</strong>
      </span>
    </v-col>
    <v-col cols="12">
      <div class="tw:hidden!" :data-loading="state.directoryListingLoading"></div>
      <v-skeleton-loader
        :loading="state.directoryListingLoading"
        type="list-item"
        style="margin: auto"
        v-if="state.directoryListingLoading"
      ></v-skeleton-loader>
      <span style="font-size: 1rem" v-else>
        Address:
        <strong>
          {{ state.directoryListings?.location.address }},
          {{ state.directoryListings?.location.city }},
          {{ state.directoryListings?.location.state }}
          {{ state.directoryListings?.location.stateName }},
          {{ state.directoryListings?.location.countryName }},
          {{ state.directoryListings?.location.countryCode }},
          {{ state.directoryListings?.location.zip }}</strong
        ></span
      >
    </v-col>
    <v-col cols="6" md="4" lg="2" class="bird-eye-listing-label-card-col">
      <LabelCard
        bottom-label="Listings"
        :count="directoryListingLabelCounts.total"
        :loading="state.directoryListingLoading"
        :color="_.includes(state.selectedListings, 'All') ? 'success' : undefined"
        @click="state.selectedListings = ['All']"
      />
    </v-col>
    <v-col cols="6" md="4" lg="2" class="bird-eye-listing-label-card-col">
      <LabelCard
        bottom-label="Synced"
        :count="directoryListingLabelCounts.synced"
        :loading="state.directoryListingLoading"
        :color="_.includes(state.selectedListings, 'Synced') ? 'success' : undefined"
        @click="handleClickDirectoryCard('Synced')"
      />
    </v-col>
    <v-col cols="6" md="4" lg="2" class="bird-eye-listing-label-card-col">
      <LabelCard
        bottom-label="Submitted"
        :count="directoryListingLabelCounts.submitted"
        :loading="state.directoryListingLoading"
        :color="_.includes(state.selectedListings, 'Submitted') ? 'success' : undefined"
        @click="handleClickDirectoryCard('Submitted')"
      />
    </v-col>
    <v-col cols="6" md="4" lg="2" class="bird-eye-listing-label-card-col">
      <LabelCard
        bottom-label="Need updates"
        :count="directoryListingLabelCounts.needUpdates"
        :loading="state.directoryListingLoading"
        :color="_.includes(state.selectedListings, 'Action Required') ? 'success' : undefined"
        @click="handleClickDirectoryCard('Action Required')"
      />
    </v-col>
    <v-col cols="6" md="4" lg="2" class="bird-eye-listing-label-card-col">
      <LabelCard
        bottom-label="Opted out"
        :count="directoryListingLabelCounts.optedOut"
        :loading="state.directoryListingLoading"
        :color="_.includes(state.selectedListings, 'Opted out') ? 'success' : undefined"
        @click="handleClickDirectoryCard('Opted out')"
      />
    </v-col>
    <v-col cols="6" md="4" lg="2" class="bird-eye-listing-label-card-col">
      <LabelCard
        bottom-label="Connected"
        :count="directoryListingLabelCounts.connect"
        :loading="state.directoryListingLoading"
        :color="_.includes(state.selectedListings, 'Connect') ? 'success' : undefined"
        @click="handleClickDirectoryCard('Connect')"
      />
    </v-col>
    <v-col cols="12" class="print-hidden">
      <v-card class="bird-eye-listings-table">
        <v-card-title class="d-flex align-center p-0">
          <v-spacer></v-spacer>

          <v-text-field
            v-model="state.directoryListingsTableSearch"
            density="compact"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            flat
            hide-details
            single-line
          ></v-text-field>
        </v-card-title>
        <div class="tw:hidden!" :data-loading="state.directoryListingLoading"></div>
        <v-data-table
          :headers="directoryListingsTable.headers"
          :items="directoryListingsTable.items"
          v-model:search="state.directoryListingsTableSearch"
          item-value="name"
          :loading="state.directoryListingLoading"
          density="comfortable"
          class="bird-eye-listings-table"
          :data-loading="state.directoryListingLoading"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              v-if="item.status === 'Need Updates' || item.status === 'Action Required'"
              color="#f33a2d"
              >Need Updates</v-chip
            >
            <v-chip v-else-if="item.status === 'Submitted'" color="#cc9c1b">{{
              item.status
            }}</v-chip>
            <v-chip v-else-if="item.status === 'Connect'" color="#54b246">Connected</v-chip>
            <v-chip v-else color="#54b246">{{ item.status }}</v-chip>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <v-col cols="12" class="print-only">
      <v-card class="bird-eye-listings-print-table">
        <v-card-title class="d-flex align-center p-0">
          <v-spacer></v-spacer>

          <v-text-field
            v-model="state.directoryListingsTableSearch"
            density="compact"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            flat
            hide-details
            single-line
          ></v-text-field>
        </v-card-title>
        <div class="tw:hidden!" :data-loading="state.directoryListingLoading"></div>
        <v-data-table
          :headers="directoryListingsPrintTable.headers"
          :items="directoryListingsPrintTable.items"
          v-model:search="state.directoryListingsTableSearch"
          item-value="name"
          :loading="state.directoryListingLoading"
          density="comfortable"
          class="bird-eye-listings-table"
          :items-per-page="directoryListingsPrintTable.items.length"
          :data-loading="state.directoryListingLoading"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              v-if="item.status === 'Need Updates' || item.status === 'Action Required'"
              color="#f33a2d"
              >Need Updates</v-chip
            >
            <v-chip v-else-if="item.status === 'Submitted'" color="#cc9c1b">{{
              item.status
            }}</v-chip>
            <v-chip v-else-if="item.status === 'Connect'" color="#54b246">Connected</v-chip>
            <v-chip v-else color="#54b246">{{ item.status }}</v-chip>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Directory Listings Charts -->
    <v-col
      v-if="chartHasNonZeroValues(googleListingReportProfileImpressionTotalChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full page-break">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <GroupedStackedChart
              v-bind="googleListingReportProfileImpressionTotalChart"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Google Impressions by Mobile and Desktop -->
    <v-col
      v-if="chartHasNonZeroValues(googleListingReportProfileImpressionsByMobileChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <GroupedStackedChart
              v-bind="googleListingReportProfileImpressionsByMobileChart"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Google User Actions by Type -->
    <v-col
      v-if="chartHasNonZeroValues(googleListingReportProfileImpressionsByDesktopChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <GroupedStackedChart
              v-bind="googleListingReportProfileImpressionsByDesktopChart"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Google User Actions by Type -->
    <v-col
      v-if="chartHasNonZeroValues(googleListingReportUserActionsByTypeChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <GroupedStackedChart
              v-bind="googleListingReportUserActionsByTypeChart"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Facebook Impressions and User Actions Charts -->
    <v-col
      v-if="chartHasNonZeroValues(facebookImpressionsTotalChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <GroupedStackedChart
              v-bind="facebookImpressionsTotalChart"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Facebook User Actions by Type -->
    <v-col v-if="chartHasNonZeroValues(facebookUserActionsChart)" cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <GroupedStackedChart
              v-bind="facebookUserActionsChart"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Bing Impressions and User Actions Charts -->
    <v-col v-if="chartHasNonZeroValues(bingImpressionsTotalChart)" cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <LineChart
              v-bind="bingImpressionsTotalChart"
              :column-width="40"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Bing User Actions by Type -->
    <v-col
      v-if="chartHasNonZeroValues(bingUserActionsByTypeChart)"
      cols="12"
      md="12"
      lg="12"
      xl="4"
    >
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.listingLoading">
            <LineChart
              v-bind="bingUserActionsByTypeChart"
              :column-width="40"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  <div
    :data="JSON.stringify(dataTotal)"
    class="tw:hidden!"
    source="Bird Eye"
    type="data-total"
  ></div>
</template>

<script setup lang="ts">
const props = defineProps<{
  account: IApp.IAccount;
  monthRange: IApp.MonthRange;
  comparedMonthRange: IApp.MonthRange;
  printMode: IApp.PrintMode;
  report?: IApp.IReport | null;
  requestId?: string;
  reportId?: string;
  onChangeSummary?: (summary: string) => void;
  mode?: IApp.PrintMode;
  showSummary: boolean;
}>();

import { computed, onMounted, reactive, ref, watch } from "vue";
import { IAnalyticsData, IApp } from "@/types";
import _ from "lodash";
import { formatToShortMonth, makeAlert, store } from "@/utils";
import BirdEyeAction from "@/actions/BirdEye";
import GroupedStackedChart, {
  type GroupedStackedChartProps
} from "../Charts/GroupedStackedChart.vue";
import type { ColumnChartProps } from "../Charts/ColumnChart.vue";
import ColumnChart from "../Charts/ColumnChart.vue";
import LabelCard from "../Widgets/LabelCard.vue";
import { useAuthStore } from "@/stores/auth";
import LineChart, { type LineChartProps } from "../LineChart.vue";
import { Symbols } from "@/constants";
import ChartLazyLoader from "@/components/ChartLazyLoader.vue";

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const state = ref<{
  reviewsAndRatingLoading: boolean;
  reviewsDistributionLoading: boolean;
  responseRatingLoading: boolean;
  reviewsAndRatingsBySourceLoading: boolean;
  listingLoading: boolean;
  directoryListings?: IAnalyticsData.IBirdEyeDirectoryListings;
  directoryListingLoading: boolean;
  directoryListingSiteListDialogActive: boolean;
  selectedListings: Array<IAnalyticsData.IBirdEyeDirectoryListings["sites"][0]["status"] | "All">;
  directoryListingsTableSearch: string;
  isDataLoaded: boolean;
  numberLoadingFinished: number;
}>({
  reviewsAndRatingLoading: true,
  reviewsDistributionLoading: true,
  responseRatingLoading: true,
  reviewsAndRatingsBySourceLoading: true,
  listingLoading: true,
  directoryListings: undefined,
  directoryListingLoading: true,
  directoryListingSiteListDialogActive: false,
  selectedListings: ["Need Updates", "Action Required"],
  directoryListingsTableSearch: "",
  isDataLoaded: false,
  numberLoadingFinished: 0
});

const emit = defineEmits<{
  (e: "onChangeData", value: Array<{ key: string; value: [] }>): void;
  (e: "onChangeDataLoad", value: { total: number; loaded: number }): void;
}>();

const dataTotal = reactive<Array<{ key: string; value: [] }>>([]);

watch(
  dataTotal,
  (newDataTotal) => {
    emit("onChangeData", newDataTotal);
  },
  { immediate: true, deep: true }
);

const createSeriesData = (x: string, y: number, label: string) => ({
  x,
  y,
  meta: {
    label
  }
});

/** Returns true if the chart has any non-zero value (should be shown), false if all values are 0 (should be hidden) */
const chartHasNonZeroValues = (chart: { series: Array<{ data?: unknown[] }> }) => {
  for (const s of chart.series) {
    const data = s.data ?? [];
    for (const item of data) {
      const val = typeof item === "number" ? item : ((item as { y?: number })?.y ?? 0);
      if (val !== 0) return true;
    }
  }
  return false;
};

const accountInfo = computed<IApp.IAccount>(() => props.account);

const startMonth = computed(() => props.monthRange.start);
const endMonth = computed(() => props.monthRange.end);
const monthRangeString = computed(
  () => `${formatToShortMonth(startMonth.value)} - ${formatToShortMonth(endMonth.value)}`
);

const comparedStartMonth = computed(() => props.comparedMonthRange.start);
const comparedEndMonth = computed(() => props.comparedMonthRange.end);

const comparedMonthRangeString = computed(
  () =>
    `${formatToShortMonth(comparedStartMonth.value)} - ${formatToShortMonth(comparedEndMonth.value)}`
);

const reviewsAndRatingAnalyticsData = ref<IAnalyticsData.IBirdEyeReviewsAndRatingsAnalyze[]>([]);
const comparedReviewsAndRatingAnalyticsData = ref<
  IAnalyticsData.IBirdEyeReviewsAndRatingsAnalyze[]
>([]);
const ratingsChartData = computed<LineChartProps>(() => ({
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: comparedReviewsAndRatingAnalyticsData.value.map((data) => ({
        y: data.rating,
        x: formatToShortMonth(data.month),
        meta: {
          month: data.month,
          label: formatToShortMonth(data.month)
        }
      }))
    },
    {
      name: `${monthRangeString.value}`,
      data: reviewsAndRatingAnalyticsData.value.map((data) => ({
        y: data.rating,
        x: formatToShortMonth(data.month),
        meta: {
          month: data.month,
          label: formatToShortMonth(data.month)
        }
      }))
    }
  ],
  title: `Ratings`,
  xAxis: reviewsAndRatingAnalyticsData.value.map((data) => formatToShortMonth(data.month)),
  loading: state.value.reviewsAndRatingLoading
}));

const reviewsChartData = computed<LineChartProps>(() => ({
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: comparedReviewsAndRatingAnalyticsData.value.map((data) => ({
        y: data.reviews,
        x: formatToShortMonth(data.month),
        meta: {
          month: data.month,
          label: formatToShortMonth(data.month)
        }
      }))
    },
    {
      name: `${monthRangeString.value}`,
      data: reviewsAndRatingAnalyticsData.value.map((data) => ({
        y: data.reviews,
        x: formatToShortMonth(data.month),
        meta: {
          month: data.month,
          label: formatToShortMonth(data.month)
        }
      }))
    }
  ],
  title: `Reviews`,
  xAxis: reviewsAndRatingAnalyticsData.value.map((data) => formatToShortMonth(data.month)),
  loading: state.value.reviewsAndRatingLoading
}));

const reviewsDistributionAnalyticsData = ref<IAnalyticsData.IBirdEyeReviewsDistributionAnalyze[]>(
  []
);
const comparedReviewsDistributionAnalyticsData = ref<
  IAnalyticsData.IBirdEyeReviewsDistributionAnalyze[]
>([]);

const reviewsDistributionChartData = computed<GroupedStackedChartProps>(() => ({
  title: `Reviews distribution`,
  series: _.map(
    [
      { name: "No Rating", rating: 0, isCompared: true },
      { name: "1 Star", rating: 1, isCompared: true },
      { name: "2 Stars", rating: 2, isCompared: true },
      { name: "3 Stars", rating: 3, isCompared: true },
      { name: "4 Stars", rating: 4, isCompared: true },
      { name: "5 Stars", rating: 5, isCompared: true },
      { name: "No Rating", rating: 0, isCompared: false },
      { name: "1 Star", rating: 1, isCompared: false },
      { name: "2 Stars", rating: 2, isCompared: false },
      { name: "3 Stars", rating: 3, isCompared: false },
      { name: "4 Stars", rating: 4, isCompared: false },
      { name: "5 Stars", rating: 5, isCompared: false }
    ],
    (item) => ({
      name: `${item.name} ${item.isCompared ? comparedMonthRangeString.value : monthRangeString.value}`,
      group: `Rating ${item.isCompared ? 1 : 0}`,
      data: _.map(
        _.map(
          item.isCompared
            ? reviewsDistributionAnalyticsData.value
            : comparedReviewsDistributionAnalyticsData.value,
          (data) => ({
            month: data.month,
            review: _.find(data.reviews, (reviewItem) => reviewItem.rating === item.rating)
          })
        ),
        (data) => ({
          y: data?.review?.count || 0,
          x: data.month,
          meta: {
            label: `${item.name}: ${formatToShortMonth(data.month)}`
          }
        })
      )
    })
  ),
  labels: reviewsDistributionAnalyticsData.value.map(
    (data, index) =>
      `${formatToShortMonth(comparedReviewsDistributionAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
  ),
  colors: ["#CCC", "#DE1B0C", "#FF6A4D", "#FBC123", "#7ED321", "#4CAE3D"],
  dataLabelEnabled: true,
  showLegend: false,
  showTotal: false
}));

const reviewsBySentimentChartData = computed<ColumnChartProps>(() => ({
  title: `Reviews by sentiment`,
  series: [
    {
      name: `Sentiment ${comparedMonthRangeString.value}`,
      type: "column",
      data: _.map(
        [
          { name: "Negative", min: 0, max: 2, color: "#DE1B0C" },
          { name: "Neutral", min: 3, max: 3, color: "#FBC123" },
          { name: "Positive", min: 4, max: 5, color: "#4CAE3D" }
        ],
        (item) => ({
          x: item.name,
          y: _.sum(
            _.map(
              _.map(comparedReviewsDistributionAnalyticsData.value, (data) =>
                _.filter(
                  data.reviews,
                  (reviewItem) => reviewItem.rating <= item.max && reviewItem.rating >= item.min
                )
              ),
              (data) => _.sumBy(data, (item) => item.count)
            )
          ),
          fillColor: item.color
        })
      )
    },
    {
      name: `Sentiment ${monthRangeString.value}`,
      type: "column",
      data: _.map(
        [
          { name: "Negative", min: 0, max: 2, color: "#DE1B0C" },
          { name: "Neutral", min: 3, max: 3, color: "#FBC123" },
          { name: "Positive", min: 4, max: 5, color: "#4CAE3D" }
        ],
        (item) => ({
          x: item.name,
          y: _.sum(
            _.map(
              _.map(reviewsDistributionAnalyticsData.value, (data) =>
                _.filter(
                  data.reviews,
                  (reviewItem) => reviewItem.rating <= item.max && reviewItem.rating >= item.min
                )
              ),
              (data) => _.sumBy(data, (item) => item.count)
            )
          ),
          fillColor: item.color
        })
      )
    }
  ],
  labels: ["Negative", "Neutral", "Positive"],
  showLabels: true
}));

const responseRateAnalyticsData = ref<IAnalyticsData.IBirdEyeResponseRateAnalyze[]>([]);
const comparedResponseRateAnalyticsData = ref<IAnalyticsData.IBirdEyeResponseRateAnalyze[]>([]);

const responseRateChartData = computed<GroupedStackedChartProps>(() => ({
  series: [
    {
      name: `Responded ${comparedMonthRangeString.value}`,
      data: comparedResponseRateAnalyticsData.value.map((data) => ({
        x: formatToShortMonth(data.month),
        y: data.response,
        meta: {
          label: `Responded: ${formatToShortMonth(data.month)}`
        }
      })),
      group: comparedMonthRangeString.value,
      type: "column"
    },
    {
      name: `Not Responded ${comparedMonthRangeString.value}`,
      data: comparedResponseRateAnalyticsData.value.map((data) => ({
        x: formatToShortMonth(data.month),
        y: data.total - data.response,
        meta: {
          label: `Not Responded: ${formatToShortMonth(data.month)}`
        }
      })),
      group: comparedMonthRangeString.value,
      type: "column"
    },
    {
      name: `Responded ${monthRangeString.value}`,
      data: responseRateAnalyticsData.value.map((data) => ({
        x: formatToShortMonth(data.month),
        y: data.response,
        meta: {
          label: `Responded: ${formatToShortMonth(data.month)}`
        }
      })),
      group: monthRangeString.value,
      type: "column"
    },
    {
      name: `Not Responded ${monthRangeString.value}`,
      data: responseRateAnalyticsData.value.map((data) => ({
        x: formatToShortMonth(data.month),
        y: data.total - data.response,
        meta: {
          label: `Not Responded: ${formatToShortMonth(data.month)}`
        }
      })),
      group: monthRangeString.value,
      type: "column"
    }
  ],
  labels: reviewsAndRatingAnalyticsData.value.map(
    (data, index) =>
      `${formatToShortMonth(comparedReviewsAndRatingAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
  ),
  title: `Response rate`,
  showLabels: true,
  dataLabelEnabled: true,
  colors: ["#09f", "#cccccc"],
  showTotal: false,
  showLegend: false
}));

const reviewsAndRatingsBySourceAnalyticsData = ref<
  IAnalyticsData.IBirdEyeReviewsAndRatingsBySourceAnalyze[]
>([]);
const reviewsAndRatingsBySourceChartData = computed<ColumnChartProps>(() => ({
  title: `Reviews & ratings by source`,
  series: [
    {
      data: _(reviewsAndRatingsBySourceAnalyticsData.value)
        .map((item) => item.reviews)
        .value(),
      type: "column",
      name: "Reviews"
    },
    {
      data: _(reviewsAndRatingsBySourceAnalyticsData.value)
        .map((item) => item.ratings)
        .value(),
      type: "column",
      name: "Ratings"
    }
  ],
  labels: _(reviewsAndRatingsBySourceAnalyticsData.value)
    .map((item) => item.source)
    .value()
}));

const listingsAnalyticsData = ref<IAnalyticsData.IBirdEyeListingsAnalyze[]>([]);
const comparedListingsAnalyticsData = ref<IAnalyticsData.IBirdEyeListingsAnalyze[]>([]);

const googleListingReportProfileImpressionTotalChartData = computed<GroupedStackedChartProps>(
  () => ({
    title: `Google Directory Listings Report - Profile Impressions Total`,
    series: [
      {
        name: `Mobile ${comparedMonthRangeString.value}`,
        group: comparedMonthRangeString.value,
        data: _(comparedListingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.mobile + data.google.impressions.search.mobile,
            meta: {
              label: `Mobile: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Desktop ${comparedMonthRangeString.value}`,
        group: comparedMonthRangeString.value,
        data: _(comparedListingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.desktop + data.google.impressions.search.desktop,
            meta: {
              label: `Desktop: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Mobile ${monthRangeString.value}`,
        group: monthRangeString.value,
        data: _(listingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.mobile + data.google.impressions.search.mobile,
            meta: {
              label: `Mobile: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Desktop ${monthRangeString.value}`,
        group: monthRangeString.value,
        data: _(listingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.desktop + data.google.impressions.search.desktop,
            meta: {
              label: `Desktop: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      }
    ],
    labels: listingsAnalyticsData.value.map(
      (data, index) =>
        `${formatToShortMonth(comparedListingsAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
    ),
    colors: ["#0099ff", "#2c3e91"],
    dataLabelEnabled: true,
    showLegend: true
  })
);

const googleListingReportProfileImpressionsByMobileChartData = computed<GroupedStackedChartProps>(
  () => ({
    title: `Google Directory Listings Report - Profile Impressions By Mobile`,
    series: [
      {
        name: `Google Search ${comparedMonthRangeString.value}`,
        group: comparedMonthRangeString.value,
        data: _(comparedListingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.search.mobile,
            meta: {
              label: `Google Search: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Google Map ${comparedMonthRangeString.value}`,
        group: comparedMonthRangeString.value,
        data: _(comparedListingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.mobile,
            meta: {
              label: `Google Map: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Google Search ${monthRangeString.value}`,
        group: monthRangeString.value,
        data: _(listingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.search.mobile,
            meta: {
              label: `Google Search: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Google Map ${monthRangeString.value}`,
        group: monthRangeString.value,
        data: _(listingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.mobile,
            meta: {
              label: `Google Map: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      }
    ],
    labels: listingsAnalyticsData.value.map(
      (data, index) =>
        `${formatToShortMonth(comparedListingsAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
    ),
    colors: ["#0099ff", "#2c3e91"],
    dataLabelEnabled: true,
    showLegend: true
  })
);

const googleListingReportProfileImpressionsByDesktopChartData = computed<GroupedStackedChartProps>(
  () => ({
    title: `Google Directory Listings Report - Profile Impressions By Desktop`,
    series: [
      {
        name: `Google Search ${comparedMonthRangeString.value}`,
        group: comparedMonthRangeString.value,
        data: _(comparedListingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.search.desktop,
            meta: {
              label: `Google Search: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Google Map ${comparedMonthRangeString.value}`,
        group: comparedMonthRangeString.value,
        data: _(comparedListingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.desktop,
            meta: {
              label: `Google Map: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Google Search ${monthRangeString.value}`,
        group: monthRangeString.value,
        data: _(listingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.search.desktop,
            meta: {
              label: `Google Search: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      },
      {
        name: `Google Map ${monthRangeString.value}`,
        group: monthRangeString.value,
        data: _(listingsAnalyticsData.value)
          .map((data) => ({
            x: formatToShortMonth(data.month),
            y: data.google.impressions.map.desktop,
            meta: {
              label: `Google Map: ${formatToShortMonth(data.month)}`
            }
          }))
          .value()
      }
    ],
    labels: listingsAnalyticsData.value.map(
      (data, index) =>
        `${formatToShortMonth(comparedListingsAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
    ),
    colors: ["#0099ff", "#2c3e91"],
    dataLabelEnabled: true,
    showLegend: true
  })
);

const googleListingReportUserActionsByTypeChartData = computed<GroupedStackedChartProps>(() => ({
  title: `Google Directory Listings Report - User Actions By Type`,
  series: [
    {
      name: `Website Visits ${comparedMonthRangeString.value}`,
      group: comparedMonthRangeString.value,
      data: _(comparedListingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.google.visits,
            `Website Visits: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: `Direction Requests ${comparedMonthRangeString.value}`,
      group: comparedMonthRangeString.value,
      data: _(comparedListingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.google.direction_requests,
            `Direction Requests: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: `Calls ${comparedMonthRangeString.value}`,
      group: comparedMonthRangeString.value,
      data: _(comparedListingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.google.calls,
            `Calls: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: `Website Visits ${monthRangeString.value}`,
      group: monthRangeString.value,
      data: _(listingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.google.visits,
            `Website Visits: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: `Direction Requests ${monthRangeString.value}`,
      group: monthRangeString.value,
      data: _(listingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.google.direction_requests,
            `Direction Requests: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: `Calls ${monthRangeString.value}`,
      group: monthRangeString.value,
      data: _(listingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.google.calls,
            `Calls: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    }
  ],
  labels: listingsAnalyticsData.value.map(
    (data, index) =>
      `${formatToShortMonth(comparedListingsAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
  ),
  colors: ["#6665dd", "#de0d92", "#fbc123"],
  dataLabelEnabled: true,
  enabledOnSeries: [],
  showLegend: true
}));

const ratingsChart = computed(() =>
  store(dataTotal, ratingsChartData.value.title, ratingsChartData.value)
);
const reviewsChart = computed(() =>
  store(dataTotal, reviewsChartData.value.title, reviewsChartData.value)
);
const reviewsDistributionChart = computed(() =>
  store(dataTotal, reviewsDistributionChartData.value.title, reviewsDistributionChartData.value)
);
const reviewsBySentimentChart = computed(() =>
  store(dataTotal, reviewsBySentimentChartData.value.title, reviewsBySentimentChartData.value)
);
const responseRateChart = computed(() =>
  store(dataTotal, responseRateChartData.value.title, responseRateChartData.value)
);
const reviewsAndRatingsBySourceChart = computed(() =>
  store(
    dataTotal,
    reviewsAndRatingsBySourceChartData.value.title,
    reviewsAndRatingsBySourceChartData.value
  )
);
const googleListingReportProfileImpressionTotalChart = computed(() =>
  store(
    dataTotal,
    googleListingReportProfileImpressionTotalChartData.value.title,
    googleListingReportProfileImpressionTotalChartData.value
  )
);
const googleListingReportProfileImpressionsByMobileChart = computed(() =>
  store(
    dataTotal,
    googleListingReportProfileImpressionsByMobileChartData.value.title,
    googleListingReportProfileImpressionsByMobileChartData.value
  )
);
const googleListingReportProfileImpressionsByDesktopChart = computed(() =>
  store(
    dataTotal,
    googleListingReportProfileImpressionsByDesktopChartData.value.title,
    googleListingReportProfileImpressionsByDesktopChartData.value
  )
);
const googleListingReportUserActionsByTypeChart = computed(() =>
  store(
    dataTotal,
    googleListingReportUserActionsByTypeChartData.value.title,
    googleListingReportUserActionsByTypeChartData.value
  )
);
const facebookImpressionsTotalChart = computed(() =>
  store(dataTotal, facebookImpressionsTotal.value.title, facebookImpressionsTotal.value)
);
const facebookUserActionsChart = computed(() =>
  store(dataTotal, facebookUserActions.value.title, facebookUserActions.value)
);
const bingImpressionsTotalChart = computed(() =>
  store(dataTotal, bingImpressionsTotal.value.title, bingImpressionsTotal.value)
);
const bingUserActionsByTypeChart = computed(() =>
  store(dataTotal, bingUserActionsByType.value.title, bingUserActionsByType.value)
);

const facebookImpressionsTotal = computed<GroupedStackedChartProps>(() => ({
  title: `Facebook Directory Listings Report - Profile Impressions Total`,
  series: [
    {
      name: `User reached ${comparedMonthRangeString.value}`,
      group: comparedMonthRangeString.value,
      data: _(comparedListingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.facebook.users_reached,
            `User reached: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: `User reached ${monthRangeString.value}`,
      group: monthRangeString.value,
      data: _(listingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.facebook.users_reached,
            `User reached: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    }
  ],
  labels: listingsAnalyticsData.value.map(
    (data, index) =>
      `${formatToShortMonth(comparedListingsAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
  ),
  colors: ["#0099ff"],
  dataLabelEnabled: true,
  showLegend: true
}));

const facebookUserActions = computed<GroupedStackedChartProps>(() => ({
  title: `Facebook Directory Listings Report - User Actions`,
  series: [
    {
      name: "User engaged",
      group: "User engaged",
      data: _(comparedListingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.facebook.users_engaged,
            `User engaged: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    },
    {
      name: "User engaged",
      group: "User engaged",
      data: _(listingsAnalyticsData.value)
        .map((data) =>
          createSeriesData(
            formatToShortMonth(data.month),
            data.facebook.users_engaged,
            `User engaged: ${formatToShortMonth(data.month)}`
          )
        )
        .value()
    }
  ],
  labels: listingsAnalyticsData.value.map(
    (data, index) =>
      `${formatToShortMonth(comparedListingsAnalyticsData.value[index].month)} ${Symbols.Compare} ${formatToShortMonth(data.month)}`
  ),
  colors: ["#0099ff"],
  dataLabelEnabled: true,
  showLegend: true
}));

const bingImpressionsTotal = computed<LineChartProps>(() => ({
  title: `Bing Directory Listings Report - Profile Impressions Total`,
  series: [
    {
      name: comparedMonthRangeString.value,
      data: comparedListingsAnalyticsData.value.map((data) => ({
        y: data.bing.users_reached,
        x: formatToShortMonth(data.month),
        meta: {
          month: data.month,
          label: formatToShortMonth(data.month)
        }
      }))
    },
    {
      name: monthRangeString.value,
      data: listingsAnalyticsData.value.map((data) => ({
        y: data.bing.users_reached,
        x: formatToShortMonth(data.month),
        meta: {
          month: data.month,
          label: formatToShortMonth(data.month)
        }
      }))
    }
  ],
  xAxis: listingsAnalyticsData.value.map((data) => formatToShortMonth(data.month)),
  loading: state.value.listingLoading
}));

const bingUserActionsByType = computed<LineChartProps>(() => {
  const userActions: Array<{
    name: string;
    key: "visits" | "direction_requests" | "calls";
  }> = [
    { name: "Website Visits", key: "visits" },
    { name: "Direction Requests", key: "direction_requests" },
    { name: "Calls", key: "calls" }
  ];

  const allSeries = [
    ...userActions.flatMap((item) => [
      {
        name: `${item.name} ${comparedMonthRangeString.value}`,
        data: comparedListingsAnalyticsData.value.map((d) => ({
          y: d.bing[item.key],
          x: formatToShortMonth(d.month),
          meta: { month: d.month, label: formatToShortMonth(d.month) }
        }))
      },
      {
        name: `${item.name} ${monthRangeString.value}`,
        data: listingsAnalyticsData.value.map((d) => ({
          y: d.bing[item.key],
          x: formatToShortMonth(d.month),
          meta: { month: d.month, label: formatToShortMonth(d.month) }
        }))
      }
    ])
  ];

  const seriesWithData = allSeries.filter((s) =>
    s.data.some((d) => (typeof d === "object" && "y" in d ? d.y : 0) !== 0)
  );

  return {
    title: `Bing Directory Listings Report - User Actions By Type`,
    series: seriesWithData.length > 0 ? seriesWithData : allSeries,
    xAxis: listingsAnalyticsData.value.map((d) => formatToShortMonth(d.month)),
    loading: state.value.listingLoading
  };
});

const directoryListingLabelCounts = computed(() => {
  const sites = state.value.directoryListings?.sites ?? [];
  return {
    total: sites.length,
    synced: sites.filter((s) => s.status === "Synced").length,
    submitted: sites.filter((s) => s.status === "Submitted").length,
    needUpdates: sites.filter((s) => s.status === "Need Updates" || s.status === "Action Required")
      .length,
    optedOut: sites.filter((s) => s.status === "Opted out").length,
    connect: sites.filter((s) => s.status === "Connect").length
  };
});

const directoryListingsTable = computed(() => {
  return {
    items: _(state.value.directoryListings?.sites ?? [])
      .filter(
        (item) =>
          _.includes(state.value.selectedListings, "All") ||
          _.includes(state.value.selectedListings, item.status)
      )
      .map((item, index) => ({
        no: index + 1,
        siteName: item.siteName,
        status: item.status,
        duplicateListingsCount: item.duplicateListingsCount
      }))
      .value(),
    headers: [
      { title: "No", key: "no" },
      { title: "Site Name", key: "siteName" },
      { title: "Status", key: "status" },
      { title: "Duplicates found", key: "duplicateListingsCount" }
    ],
    filter(value: string, query: string) {
      if (!_.isString(value)) {
        return false;
      }
      return _.indexOf(value.toLowerCase(), query?.toLowerCase()) !== -1;
    }
  };
});

const directoryListingsPrintTable = computed(() => {
  return {
    items: _(state.value.directoryListings?.sites ?? [])
      .map((item, index) => ({
        no: index + 1,
        siteName: item.siteName,
        status: item.status,
        duplicateListingsCount: item.duplicateListingsCount
      }))
      .value(),
    headers: [
      { title: "No", key: "no" },
      { title: "Site Name", key: "siteName" },
      { title: "Status", key: "status" },
      { title: "Duplicates found", key: "duplicateListingsCount" }
    ],
    filter(value: string, query: string) {
      if (!_.isString(value)) {
        return false;
      }
      return _.indexOf(value.toLowerCase(), query?.toLowerCase()) !== -1;
    }
  };
});

const setAllLoading = (loading: boolean) => {
  state.value.reviewsAndRatingLoading = loading;
  state.value.reviewsDistributionLoading = loading;
  state.value.responseRatingLoading = loading;
  state.value.reviewsAndRatingsBySourceLoading = loading;
  state.value.listingLoading = loading;
  state.value.directoryListingLoading = loading;
};

const fetchAllData = async () => {
  const userId = user.value?.id;
  if (!userId) return;
  setAllLoading(true);

  const response = await BirdEyeAction.getAllAnalyticsData(
    userId,
    accountInfo.value.customerId,
    accountInfo.value.account,
    {
      startMonth: startMonth.value,
      endMonth: endMonth.value,
      comparedStartMonth: comparedStartMonth.value,
      comparedEndMonth: comparedEndMonth.value
    }
  );

  if (response.status) {
    const d = response.data as Record<string, unknown>;

    if (d["reviews-and-ratings"])
      reviewsAndRatingAnalyticsData.value = d[
        "reviews-and-ratings"
      ] as typeof reviewsAndRatingAnalyticsData.value;
    if (d["reviews-and-ratings-compared"])
      comparedReviewsAndRatingAnalyticsData.value = d[
        "reviews-and-ratings-compared"
      ] as typeof comparedReviewsAndRatingAnalyticsData.value;
    state.value.reviewsAndRatingLoading = false;

    if (d["reviews-distribution"])
      reviewsDistributionAnalyticsData.value = d[
        "reviews-distribution"
      ] as typeof reviewsDistributionAnalyticsData.value;
    if (d["reviews-distribution-compared"])
      comparedReviewsDistributionAnalyticsData.value = d[
        "reviews-distribution-compared"
      ] as typeof comparedReviewsDistributionAnalyticsData.value;
    state.value.reviewsDistributionLoading = false;

    if (d["response-rate"])
      responseRateAnalyticsData.value = d[
        "response-rate"
      ] as typeof responseRateAnalyticsData.value;
    if (d["response-rate-compared"])
      comparedResponseRateAnalyticsData.value = d[
        "response-rate-compared"
      ] as typeof comparedResponseRateAnalyticsData.value;
    state.value.responseRatingLoading = false;

    if (d["reviews-and-ratings-by-source"])
      reviewsAndRatingsBySourceAnalyticsData.value = d[
        "reviews-and-ratings-by-source"
      ] as typeof reviewsAndRatingsBySourceAnalyticsData.value;
    state.value.reviewsAndRatingsBySourceLoading = false;

    if (d["listings"])
      listingsAnalyticsData.value = d["listings"] as typeof listingsAnalyticsData.value;
    if (d["listings-compared"])
      comparedListingsAnalyticsData.value = d[
        "listings-compared"
      ] as typeof comparedListingsAnalyticsData.value;
    state.value.listingLoading = false;

    if (d["directory-listings"])
      state.value.directoryListings = d[
        "directory-listings"
      ] as IAnalyticsData.IBirdEyeDirectoryListings;
    state.value.directoryListingLoading = false;
  } else {
    makeAlert(response.message, "error");
    setAllLoading(false);
  }
};

const handleClickDirectoryCard = (
  type: IAnalyticsData.IBirdEyeDirectoryListings["sites"][0]["status"] | "All"
) => {
  state.value.selectedListings = [type];
  // if (type === "All") {
  //   return;
  // }
  // if (_.includes(state.value.selectedListings, type)) {
  //   state.value.selectedListings = _(state.value.selectedListings)
  //     .without(type)
  //     .without("All")
  //     .value();
  // } else {
  //   state.value.selectedListings = _.without([...state.value.selectedListings, type], "All");
  // }
};

onMounted(() => {
  reFetch();
});
watch([startMonth, endMonth, comparedStartMonth, comparedEndMonth], () => {
  state.value.isDataLoaded = false;
  reFetch();
});

async function reFetch() {
  try {
    state.value.numberLoadingFinished = 0;
    await fetchAllData();
    state.value.numberLoadingFinished = 6;
    emit("onChangeDataLoad", { total: 6, loaded: 6 });
    state.value.isDataLoaded = true;
  } catch {
    makeAlert("Failed to load BirdEye analytics data", "error");
  }
}
</script>

<style>
.bird-eye-listings-table .v-input__details {
  display: none;
}

.bird-eye-listings-table .v-card-title {
  padding: 0.5rem 1rem !important;
  margin: 0 !important;
}

.bird-eye-listings-table .v-input {
  width: 300px !important;
  max-width: 300px !important;
}
</style>
